import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Award, Plus, Calendar, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { format, isAfter, isBefore, addDays } from "date-fns";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface Certification {
  id: string;
  certification_name: string;
  certification_type: string;
  issued_date: string | null;
  expires_date: string | null;
  issuing_organization: string | null;
  certification_number: string | null;
  verification_status: string;
  is_required: boolean;
  notes: string | null;
  renewal_reminder_days: number;
}

interface EmployeeCertificationViewProps {
  employee: Employee;
  onDataUpdate: () => void;
}

export const EmployeeCertificationView: React.FC<EmployeeCertificationViewProps> = ({
  employee,
  onDataUpdate
}) => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [certForm, setCertForm] = useState({
    certification_name: '',
    certification_type: '',
    issued_date: '',
    expires_date: '',
    issuing_organization: '',
    certification_number: '',
    verification_status: 'pending',
    is_required: false,
    notes: '',
    renewal_reminder_days: 30
  });

  const certificationTypes = [
    'safety',
    'technical',
    'professional',
    'license',
    'training',
    'compliance',
    'other'
  ];

  useEffect(() => {
    fetchCertifications();
  }, [employee.id]);

  const fetchCertifications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employee_certifications')
        .select('*')
        .eq('employee_id', employee.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCertifications(data || []);
    } catch (error) {
      console.error('Error fetching certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCertification = async () => {
    setFormLoading(true);
    try {
      const { error } = await supabase
        .from('employee_certifications')
        .insert([{
          employee_id: employee.id,
          certification_name: certForm.certification_name,
          certification_type: certForm.certification_type,
          issued_date: certForm.issued_date || null,
          expires_date: certForm.expires_date || null,
          issuing_organization: certForm.issuing_organization || null,
          certification_number: certForm.certification_number || null,
          verification_status: certForm.verification_status,
          is_required: certForm.is_required,
          notes: certForm.notes || null,
          renewal_reminder_days: certForm.renewal_reminder_days
        }]);

      if (error) throw error;

      toast.success('Certification added successfully');
      setAddDialogOpen(false);
      setCertForm({
        certification_name: '',
        certification_type: '',
        issued_date: '',
        expires_date: '',
        issuing_organization: '',
        certification_number: '',
        verification_status: 'pending',
        is_required: false,
        notes: '',
        renewal_reminder_days: 30
      });
      fetchCertifications();
      onDataUpdate();
    } catch (error) {
      console.error('Error adding certification:', error);
      toast.error('Failed to add certification');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const isExpiringSoon = (expiresDate: string | null, reminderDays: number) => {
    if (!expiresDate) return false;
    const expireDate = new Date(expiresDate);
    const reminderDate = addDays(new Date(), reminderDays);
    return isAfter(reminderDate, expireDate);
  };

  const isExpired = (expiresDate: string | null) => {
    if (!expiresDate) return false;
    return isBefore(new Date(expiresDate), new Date());
  };

  const getCertificationStatus = (cert: Certification) => {
    if (cert.expires_date) {
      if (isExpired(cert.expires_date)) {
        return 'expired';
      }
      if (isExpiringSoon(cert.expires_date, cert.renewal_reminder_days)) {
        return 'expiring_soon';
      }
    }
    return cert.verification_status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Certifications & Training</h3>
          <p className="text-sm text-muted-foreground">
            Track employee certifications, licenses, and training records
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Certification
        </Button>
      </div>

      {/* Certifications Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-xl font-bold">
                  {certifications.filter(c => getCertificationStatus(c) === 'verified').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-xl font-bold">
                  {certifications.filter(c => getCertificationStatus(c) === 'expiring_soon').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Clock className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="text-xl font-bold">
                  {certifications.filter(c => getCertificationStatus(c) === 'expired').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certifications List */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      ) : certifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Award className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Certifications Yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Add certifications to track {employee.first_name}'s qualifications
            </p>
            <Button onClick={() => setAddDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add First Certification
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certifications.map((cert) => {
            const status = getCertificationStatus(cert);
            return (
              <Card key={cert.id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{cert.certification_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {cert.issuing_organization || 'No organization specified'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cert.is_required && (
                        <Badge variant="outline" className="text-xs">Required</Badge>
                      )}
                      <Badge className={getStatusColor(status)} variant="secondary">
                        <div className="flex items-center gap-1">
                          {getStatusIcon(status)}
                          <span className="capitalize">{status.replace('_', ' ')}</span>
                        </div>
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {cert.certification_type.replace('_', ' ')}
                      </Badge>
                      {cert.certification_number && (
                        <span className="text-muted-foreground">#{cert.certification_number}</span>
                      )}
                    </div>
                    
                    {cert.issued_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          Issued: {format(new Date(cert.issued_date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    )}
                    
                    {cert.expires_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span 
                          className={
                            isExpired(cert.expires_date) 
                              ? 'text-red-600' 
                              : isExpiringSoon(cert.expires_date, cert.renewal_reminder_days)
                              ? 'text-orange-600'
                              : 'text-muted-foreground'
                          }
                        >
                          Expires: {format(new Date(cert.expires_date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    )}
                    
                    {cert.notes && (
                      <p className="text-muted-foreground text-xs mt-2">{cert.notes}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Certification Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Certification</DialogTitle>
            <DialogDescription>
              Add a new certification or training record for {employee.first_name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="cert_name">Certification Name</Label>
              <Input
                id="cert_name"
                value={certForm.certification_name}
                onChange={(e) => setCertForm(prev => ({ ...prev, certification_name: e.target.value }))}
                placeholder="e.g., CPR Certification"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="cert_type">Type</Label>
              <Select value={certForm.certification_type} onValueChange={(value) => setCertForm(prev => ({ ...prev, certification_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {certificationTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="organization">Issuing Organization</Label>
              <Input
                id="organization"
                value={certForm.issuing_organization}
                onChange={(e) => setCertForm(prev => ({ ...prev, issuing_organization: e.target.value }))}
                placeholder="e.g., American Red Cross"
              />
            </div>

            <div>
              <Label htmlFor="cert_number">Certification Number</Label>
              <Input
                id="cert_number"
                value={certForm.certification_number}
                onChange={(e) => setCertForm(prev => ({ ...prev, certification_number: e.target.value }))}
                placeholder="Certificate ID or number"
              />
            </div>

            <div>
              <Label htmlFor="status">Verification Status</Label>
              <Select value={certForm.verification_status} onValueChange={(value) => setCertForm(prev => ({ ...prev, verification_status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="issued_date">Issued Date</Label>
              <Input
                id="issued_date"
                type="date"
                value={certForm.issued_date}
                onChange={(e) => setCertForm(prev => ({ ...prev, issued_date: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="expires_date">Expiration Date</Label>
              <Input
                id="expires_date"
                type="date"
                value={certForm.expires_date}
                onChange={(e) => setCertForm(prev => ({ ...prev, expires_date: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="reminder_days">Reminder (days before expiry)</Label>
              <Input
                id="reminder_days"
                type="number"
                value={certForm.renewal_reminder_days}
                onChange={(e) => setCertForm(prev => ({ ...prev, renewal_reminder_days: parseInt(e.target.value) || 30 }))}
                min="1"
                max="365"
              />
            </div>

            <div className="col-span-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={certForm.is_required}
                  onChange={(e) => setCertForm(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_required">This is a required certification</Label>
              </div>
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={certForm.notes}
                onChange={(e) => setCertForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about this certification..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCertification} 
              disabled={formLoading || !certForm.certification_name || !certForm.certification_type}
            >
              {formLoading ? 'Adding...' : 'Add Certification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};